import DOMPurify from 'dompurify'
import { FC, Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add, Comment, Edit, ExpandLess, ExpandMore, HelpOutline, Warning } from '@mui/icons-material'
import {
  alpha,
  Box,
  Button,
  Card,
  Collapse,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { AnalysisNote, AnalysisNotesMap } from '../../../../api/analysis-notes'
import { InternalGameScreenshot } from '../../../../api/screenshot-links'
import { InternalCategoryFeature } from '../../../../types/InternalCategory'
import { FeatureExamplePosition } from '../../../categories/types/Categories'
import { differsFromBenchmarks, getBenchmarkChoice, getFeatureScreenshots, isFutureFeature } from '../../helpers/featureChoicesHelpers'
import { BenchmarkColumn } from '../../hooks/useBenchmarkColumns'
import { ActiveFeatures, CategoryAnswers } from '../../types/InternalAnalysis'
import { BenchmarkAnswerCell } from '../BenchmarkAnswerCell/BenchmarkAnswerCell'
import { FeatureChoiceAnswer } from '../FeatureChoiceAnswer/FeatureChoiceAnswer'
import { FeatureExample } from '../FeatureExample/FeatureExample'

type QueryState<T> = { data?: T; isLoading: boolean; isError: boolean }

export type ScreenshotsTarget = { gameName: string; featureName: string; screenshots: InternalGameScreenshot[] }

type FeatureChoicesTableProps = {
  features: InternalCategoryFeature[]
  answers: CategoryAnswers
  gameName: string
  position: FeatureExamplePosition
  activeFeatures?: ActiveFeatures
  benchmarkColumns: BenchmarkColumn[]
  screenshots: QueryState<InternalGameScreenshot[]>
  notes: QueryState<AnalysisNotesMap>
  disabled?: boolean
  onSelect: (featureId: string, weightId?: number) => void
  onToggle: (featureId: string, weightId: number) => void
  onOpenScreenshots: (target: ScreenshotsTarget) => void
  onEditNote: (feature: InternalCategoryFeature, note?: AnalysisNote) => void
  onViewBenchmarkNote: (column: BenchmarkColumn, feature: InternalCategoryFeature, note: AnalysisNote) => void
}

export const FeatureChoicesTable: FC<FeatureChoicesTableProps> = ({
  features,
  answers,
  gameName,
  position,
  activeFeatures,
  benchmarkColumns,
  screenshots,
  notes,
  disabled,
  onSelect,
  onToggle,
  onOpenScreenshots,
  onEditNote,
  onViewBenchmarkNote,
}) => {
  const { t } = useTranslation()
  const [openExamples, setOpenExamples] = useState<Set<string>>(new Set())
  const columnCount = 5 + benchmarkColumns.length
  const benchmarkAnalyses = benchmarkColumns.map(({ analysis }) => analysis)

  const toggleExample = (featureId: string) =>
    setOpenExamples((current) => {
      const next = new Set(current)
      if (!next.delete(featureId)) next.add(featureId)
      return next
    })

  const renderScreenshots = (feature: InternalCategoryFeature) => {
    if (screenshots.isLoading) return <Skeleton width={96} />
    if (screenshots.isError) return <Typography color="error.main">{t('internal-analysts:analysis_choices_screenshots_failed')}</Typography>
    const featureScreenshots = getFeatureScreenshots(screenshots.data, feature.legacyId)
    const label = t('internal-analysts:analysis_choices_screenshots', { count: featureScreenshots.length })
    if (!featureScreenshots.length) return <Typography variant="body2">{label}</Typography>
    return (
      <Link
        component="button"
        variant="body2"
        fontWeight="bold"
        onClick={() => onOpenScreenshots({ gameName, featureName: feature.name, screenshots: featureScreenshots })}
      >
        {label}
      </Link>
    )
  }

  const renderNote = (feature: InternalCategoryFeature) => {
    if (notes.isLoading) return <Skeleton width={64} />
    if (notes.isError) return <Typography color="error.main">{t('internal-analysts:analysis_choices_notes_failed')}</Typography>
    const note = notes.data?.[feature.legacyId]
    if (!note) {
      return (
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => onEditNote(feature)}
          aria-label={t('internal-analysts:analysis_choices_add_note_for', { name: feature.name })}
        >
          {t('internal-analysts:analysis_choices_add_note')}
        </Button>
      )
    }
    return (
      <Stack direction="row" alignItems="center">
        <Button
          size="small"
          startIcon={<Edit />}
          onClick={() => onEditNote(feature, note)}
          aria-label={t('internal-analysts:analysis_choices_edit_note_for', { name: feature.name })}
        >
          {t('internal-analysts:analysis_choices_edit_note')}
        </Button>
        <GRTooltip content={<Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.notes ?? '') }} />}>
          <Comment color="success" fontSize="small" aria-label={t('internal-analysts:analysis_choices_has_note')} />
        </GRTooltip>
      </Stack>
    )
  }

  return (
    <TableContainer component={Card} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('internal-analysts:analysis_choices_feature')}</TableCell>
            <TableCell align="center">
              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                <span>{t('internal-analysts:analysis_choices_examples')}</span>
                <GRTooltip content={t('internal-analysts:analysis_choices_examples_help')}>
                  <HelpOutline fontSize="inherit" color="action" />
                </GRTooltip>
              </Stack>
            </TableCell>
            <TableCell sx={{ minWidth: 220 }}>{t('internal-analysts:analysis_choices_answer')}</TableCell>
            {benchmarkColumns.map((column) => (
              <TableCell key={`${column.benchmark.gameId}-${column.benchmark.marketIso}`} sx={{ minWidth: 140 }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>{`${column.gameName} (${column.benchmark.marketIso.toUpperCase()})`}</span>
                  {column.failed && (
                    <GRTooltip content={t('internal-analysts:analysis_choices_benchmark_failed')}>
                      <Warning color="warning" fontSize="small" aria-label={t('internal-analysts:analysis_choices_benchmark_failed')} />
                    </GRTooltip>
                  )}
                </Stack>
              </TableCell>
            ))}
            <TableCell align="center">{t('internal-analysts:analysis_choices_implementations')}</TableCell>
            <TableCell>{t('internal-analysts:analysis_choices_notes')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {features.map((feature) => {
            const isOpen = openExamples.has(feature.id)
            const differs = differsFromBenchmarks(feature, answers, benchmarkAnalyses)
            const isFutureUnanswered = isFutureFeature(feature, activeFeatures) && answers[feature.id] === undefined
            return (
              <Fragment key={feature.id}>
                <TableRow
                  data-testid={`feature-row-${feature.id}`}
                  data-differs={differs || undefined}
                  data-future={isFutureUnanswered || undefined}
                  sx={{
                    '& > td': { borderBottom: isOpen ? 0 : undefined },
                    bgcolor: (theme) =>
                      differs ? alpha(theme.palette.warning.main, 0.15) : isFutureUnanswered ? theme.palette.action.disabledBackground : undefined,
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" color={isFutureUnanswered ? 'text.disabled' : undefined}>
                      {feature.name}
                    </Typography>
                    {isFutureUnanswered && (
                      <Typography variant="caption" color="text.secondary">
                        {t('internal-analysts:analysis_choices_future_feature')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      endIcon={isOpen ? <ExpandLess /> : <ExpandMore />}
                      aria-expanded={isOpen}
                      aria-label={t('internal-analysts:analysis_choices_examples_for', { name: feature.name })}
                      onClick={() => toggleExample(feature.id)}
                    >
                      {t(isOpen ? 'internal-analysts:analysis_choices_close' : 'internal-analysts:analysis_choices_open')}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <FeatureChoiceAnswer
                      feature={feature}
                      answer={answers[feature.id]}
                      disabled={disabled}
                      onSelect={(weightId) => onSelect(feature.id, weightId)}
                      onToggle={(weightId) => onToggle(feature.id, weightId)}
                    />
                  </TableCell>
                  {benchmarkColumns.map((column) => {
                    const note = column.notes?.[feature.legacyId]
                    const benchmarkScreenshots = getFeatureScreenshots(column.screenshots, feature.legacyId)
                    return (
                      <TableCell key={`${column.benchmark.gameId}-${column.benchmark.marketIso}`}>
                        <BenchmarkAnswerCell
                          choice={getBenchmarkChoice(feature, column.analysis)?.choice}
                          notes={note?.notes}
                          screenshotCount={benchmarkScreenshots.length}
                          gameName={column.gameName}
                          onViewNote={() => note && onViewBenchmarkNote(column, feature, note)}
                          onOpenScreenshots={() =>
                            onOpenScreenshots({ gameName: column.gameName, featureName: feature.name, screenshots: benchmarkScreenshots })
                          }
                        />
                      </TableCell>
                    )
                  })}
                  <TableCell align="center">{renderScreenshots(feature)}</TableCell>
                  <TableCell>{renderNote(feature)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ py: 0, borderBottom: isOpen ? undefined : 0 }}>
                    <Collapse in={isOpen} unmountOnExit>
                      <Box py={2}>
                        <FeatureExample featureId={feature.id} position={position} />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
